
import React, { useState } from 'react';
import './CreatePostModal.css';
import { useApp } from '../../context/AppContext';

interface CreatePostModalProps {
  onClose: () => void;
}

const CreatePostModal: React.FC<CreatePostModalProps> = ({ onClose }) => {
  const { createPost, adminUser } = useApp();
  const [content, setContent] = useState('');
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'text'>('text');
  const [preview, setPreview] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [price, setPrice] = useState('9.99');
  const [posting, setPosting] = useState(false);
  const [success, setSuccess] = useState(false);

  const generatePlaceholder = (type: string) => {
    const colors = ['#6366f1', '#8b5cf6', '#ec4899'];
    const color = colors[Math.floor(Math.random() * colors.length)];
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400">
        <defs>
          <linearGradient id="postGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${color};stop-opacity:0.9" />
            <stop offset="100%" style="stop-color:#0f172a;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="400" height="400" fill="url(#postGrad)"/>
        <text x="200" y="200" font-size="48" text-anchor="middle" fill="white" font-family="Arial">${type === 'video' ? '🎬' : '📷'}</text>
      </svg>
    `)}`;
  };

  const handleSelectMedia = () => {
    if (mediaType !== 'text') {
      setPreview(generatePlaceholder(mediaType));
    }
  };

  const handlePost = () => {
    if (!content.trim() && mediaType === 'text') return;
    
    setPosting(true);
    setTimeout(() => {
      createPost({
        content,
        mediaType,
        mediaUrl: preview || undefined,
        isLocked,
        price: isLocked ? parseFloat(price) : undefined,
        isStory: false
      });
      setSuccess(true);
      setTimeout(onClose, 1500);
    }, 1000);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-post-modal animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />
        
        {success ? (
          <div className="post-success">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h3>Post Published!</h3>
            <p>Your subscribers can now see your post</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Create Post</h2>
              <button className="close-btn" onClick={onClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="post-author">
              <img src={adminUser.avatar} alt={adminUser.displayName} />
              <div>
                <span className="author-name">{adminUser.displayName}</span>
                <span className="post-visibility">
                  {isLocked ? '🔒 Premium Only' : '🌍 All Subscribers'}
                </span>
              </div>
            </div>

            <textarea
              className="post-content-input"
              placeholder="What's on your mind?"
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={4}
            />

            <div className="media-type-tabs">
              <button 
                className={`type-tab ${mediaType === 'text' ? 'active' : ''}`}
                onClick={() => { setMediaType('text'); setPreview(null); }}
              >
                📝 Text
              </button>
              <button 
                className={`type-tab ${mediaType === 'image' ? 'active' : ''}`}
                onClick={() => setMediaType('image')}
              >
                📷 Photo
              </button>
              <button 
                className={`type-tab ${mediaType === 'video' ? 'active' : ''}`}
                onClick={() => setMediaType('video')}
              >
                🎬 Video
              </button>
            </div>

            {mediaType !== 'text' && (
              <div className="media-preview-area" onClick={handleSelectMedia}>
                {preview ? (
                  <img src={preview} alt="Post preview" />
                ) : (
                  <div className="upload-placeholder">
                    <div className="upload-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                    </div>
                    <span>Tap to add {mediaType}</span>
                  </div>
                )}
              </div>
            )}

            <div className="post-options">
              <label className="post-option">
                <div>
                  <span className="option-label">🔒 Premium Content</span>
                  <span className="option-desc">Only paying subscribers can view</span>
                </div>
                <input 
                  type="checkbox" 
                  checked={isLocked}
                  onChange={e => setIsLocked(e.target.checked)}
                />
              </label>
              
              {isLocked && (
                <div className="price-input-group">
                  <span className="currency">$</span>
                  <input
                    type="number"
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                    placeholder="9.99"
                    step="0.01"
                    min="0.99"
                  />
                </div>
              )}
            </div>

            <button 
              className={`publish-btn ${posting ? 'posting' : ''}`}
              onClick={handlePost}
              disabled={(mediaType === 'text' && !content.trim()) || (mediaType !== 'text' && !preview) || posting}
            >
              {posting ? (
                <span className="spinner" />
              ) : (
                isLocked ? `Publish Premium Post - $${price}` : 'Publish Post'
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CreatePostModal;
